import type { JSX } from 'react';
import {
  AGENTS,
  EFFORTS,
  agentForModel,
  effortsFor,
  enabledModelsForAgent,
  isHiddenAgent,
  modelsForAgent,
  type EffortId,
  type ModelId,
} from '../lib/models.ts';
import { useEnabledModelIds } from '../lib/model-catalog.ts';
import type { HostPref } from '../lib/protocol.ts';
import type { ProjectRow } from '../lib/project.ts';
import { ChoiceGroup } from './ChoiceGroup.tsx';
import { HostSwitcher } from './ConfigView.tsx';

const LOGGING_OPTIONS = [
  { id: 'off', label: 'Off' },
  { id: 'on', label: 'On' },
] as const;

export function SettingsView({
  model,
  effort,
  hidden,
  project,
  hostBusy,
  logging = false,
  onModel,
  onEffort,
  onPreferredHost,
  onLogging,
}: {
  model: ModelId;
  effort: EffortId | null;
  hidden?: boolean;
  project?: ProjectRow | null;
  hostBusy?: boolean;
  logging?: boolean;
  onModel: (model: ModelId) => void;
  onEffort: (effort: EffortId) => void;
  onPreferredHost?: (preferredHost: HostPref) => void;
  onLogging?: (logging: boolean) => void;
}): JSX.Element {
  const rungs = effortsFor(model).map((id) => EFFORTS.find((e) => e.id === id)!);
  const agent = AGENTS.find((a) => a.id === agentForModel(model));
  const enabled = useEnabledModelIds();
  // The chat's own model stays pickable even when More → Models turned it off,
  // so an older chat never shows an empty backend.
  const optionsFor = (id: (typeof AGENTS)[number]['id']): readonly { id: ModelId; label: string }[] => {
    const list = enabled.length === 0 ? modelsForAgent(id) : enabledModelsForAgent(id, enabled);
    if (agent?.id !== id || list.some((m) => m.id === model)) return list;
    return modelsForAgent(id).filter((m) => m.id === model || list.some((l) => l.id === m.id));
  };

  return (
    <div
      className="settings-pane"
      id="pane-settings"
      role="tabpanel"
      aria-labelledby="tab-settings"
      hidden={hidden}
    >
      {project?.desktop && onPreferredHost && (
        <section className="git-section">
          <div className="git-section-title">Host</div>
          <HostSwitcher
            project={project}
            disabled={hostBusy}
            onChange={onPreferredHost}
          />
          <div className="settings-hint">
            Agents, Git, and Files use this clone. Deploy always runs on the server.
          </div>
        </section>
      )}
      {AGENTS.filter(
        (a) =>
          !isHiddenAgent(a.id) ||
          a.id === agent?.id ||
          enabledModelsForAgent(a.id, enabled).length > 0,
      )
        .map((a) => ({ agent: a, options: optionsFor(a.id) }))
        .filter(({ options }) => options.length > 0)
        .map(({ agent: a, options }) => (
          <section key={a.id} className="git-section">
            <div className="git-section-title">{a.label}</div>
            <ChoiceGroup
              label={`${a.label} models`}
              value={model}
              options={options}
              onChange={onModel}
            />
            {agent?.id === a.id && <div className="settings-hint">Runs through the {a.label} CLI.</div>}
          </section>
        ))}
      {onLogging && (
        <section className="git-section">
          <div className="git-section-title">Logging</div>
          <ChoiceGroup
            label="Logging"
            value={logging ? 'on' : 'off'}
            options={LOGGING_OPTIONS}
            onChange={(id) => onLogging(id === 'on')}
          />
          <div className="settings-hint">
            Writes this chat — prompts, tool calls, answers — to a file in the
            gateway's <code>logs/</code> folder, to read back later.
          </div>
        </section>
      )}
      {rungs.length > 0 && effort && (
        <section className="git-section">
          <div className="git-section-title">Effort</div>
          <ChoiceGroup label="Effort" value={effort} options={rungs} onChange={onEffort} />
          <div className="settings-hint">
            {agent?.id === 'claude'
              ? 'Thinking budget for the next message you send.'
              : 'Used for the next message you send.'}
          </div>
        </section>
      )}
    </div>
  );
}
